import React, { useEffect, useState } from 'react';
import {
  adminToken, adminLogin, adminMe,
  adminListProducts, adminUpdateProductImage,
} from '../lib/adminApi';

function Login({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await adminLogin(email, password); onSuccess(); }
    catch (e2) { setErr(e2.response?.data?.detail || 'Login failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-purple-50 p-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md bg-white/80 backdrop-blur rounded-lg border border-neutral-900/10 p-8 shadow-xl">
        <h1 className="font-display uppercase text-3xl text-[color:var(--alfa-navy)]">ALFÀ Admin</h1>
        <p className="mt-2 text-sm text-neutral-950">Sign in to manage product images.</p>
        <label className="block mt-6 text-xs uppercase tracking-widest text-neutral-950">Email</label>
        <input data-testid="admin-login-email" autoFocus type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
        <label className="block mt-4 text-xs uppercase tracking-widest text-neutral-950">Password</label>
        <input data-testid="admin-login-password" type="password" required value={password} onChange={(e)=>setPw(e.target.value)} className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
        {err && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">{err}</p>}
        <button data-testid="admin-login-submit" type="submit" disabled={busy} className="mt-6 w-full bg-neutral-950 text-white uppercase text-xs tracking-widest font-semibold py-3 rounded-sm disabled:opacity-60">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

function ImageEditRow({ label, currentUrl, onSave, testid }) {
  const [url, setUrl] = useState(currentUrl || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const save = async () => {
    setBusy(true); setMsg('');
    try { await onSave(url); setMsg('Saved ✓'); }
    catch (e) { setMsg(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); setTimeout(()=>setMsg(''), 2500); }
  };
  return (
    <div data-testid={testid} className="flex items-center gap-3 py-3 border-b">
      {url && <img src={url} alt="" className="w-20 h-14 object-cover rounded border" />}
      <div className="flex-1">
        <p className="font-display uppercase text-sm text-neutral-900">{label}</p>
        <input value={url} onChange={(e)=>setUrl(e.target.value)} placeholder="/images/… or https://…" className="mt-1 w-full border rounded px-2 py-1 text-xs font-mono" />
      </div>
      <button onClick={save} disabled={busy} className="bg-neutral-950 text-white uppercase text-xs tracking-widest px-3 py-2 rounded-sm disabled:opacity-60">{busy ? '…' : 'Save'}</button>
      {msg && <span className="text-xs text-emerald-700 ml-2">{msg}</span>}
    </div>
  );
}

function Dashboard({ email, onLogout }) {
  const [products, setProducts] = useState([]);
  const refresh = async () => setProducts(await adminListProducts());
  useEffect(() => { refresh(); }, []);

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-neutral-950 text-white px-6 py-4 flex items-center justify-between">
        <p className="font-display uppercase text-lg tracking-widest">ALFÀ Admin</p>
        <div className="text-xs flex items-center gap-4">
          <a href="/admin/submissions" data-testid="admin-link-submissions" className="uppercase tracking-widest hover:underline">Submissions</a>
          <span className="opacity-70">{email}</span>
          <button data-testid="admin-logout" onClick={onLogout} className="uppercase tracking-widest hover:underline">Sign out</button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto p-6">
        <div data-testid="admin-products-panel" className="bg-white rounded-lg border p-4">
          <p className="text-xs uppercase tracking-widest text-neutral-950 mb-3">Weight Loss treatments — product photos</p>
          {products.map((p) => (
            <ImageEditRow
              key={p.slug}
              label={p.name}
              currentUrl={p.img}
              onSave={(url) => adminUpdateProductImage(p.slug, url).then(refresh)}
              testid={`admin-product-row-${p.slug}`}
            />
          ))}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [me, setMe] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      if (!adminToken.get()) { setLoaded(true); return; }
      try { setMe(await adminMe()); }
      catch { adminToken.clear(); }
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return <div className="min-h-screen flex items-center justify-center text-neutral-950">Loading…</div>;
  if (!me) return <Login onSuccess={async () => setMe(await adminMe())} />;
  return <Dashboard email={me.email} onLogout={() => { adminToken.clear(); setMe(null); }} />;
}
